import { ControlOverLegalClientStatusType } from 'api/types/entity/legal/beneficiary/controlOverEntity';
import { FormContainer, FormContainerStepProps } from 'components';
import { getYesOrNoOptions } from 'helpers';
import { useTranslation } from 'libs/i18n';

import {
  ControlOverFormType,
  getSchema,
  FormErrors,
  Field,
} from '../../forms/controlOverForm';

interface Props extends FormContainerStepProps {
  initialValues?: ControlOverFormType;
  canGoBack?: boolean;
  formError?: FormErrors;
  submitLoading?: boolean;
  enabledFinancialConditionAmount: boolean;
  enabledControlOverLegalClientStatus: boolean;
  enabledHasControlOverLegalClient: boolean;
  onSubmit: (values: ControlOverFormType) => void;
  onGoBack?: () => void;
}

export const ControlOver = ({
  initialValues,
  submitLoading,
  formError,
  canGoBack,
  step,
  enabledControlOverLegalClientStatus,
  enabledFinancialConditionAmount,
  enabledHasControlOverLegalClient,
  onGoBack,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const schema = getSchema({
    enabledControlOverLegalClientStatus,
    enabledFinancialConditionAmount,
    enabledHasControlOverLegalClient,
  });
  return (
    <FormContainer
      canGoBack={canGoBack}
      initialErrors={formError}
      initialValues={initialValues}
      step={step}
      submitLoading={submitLoading}
      subTitle={t('onboarding.legalClientBeneficiary.controlOver.subtitle')}
      title={t('onboarding.legalClientBeneficiary.controlOver.title')}
      validationSchema={schema}
      onGoBack={onGoBack}
      onSubmit={onSubmit}
    >
      {enabledControlOverLegalClientStatus && (
        <Field.Autocomplete
          label={t(
            'onboarding.legalClientBeneficiary.controlOver.controlOverLegalClientStatus.label'
          )}
          name="controlOverLegalClientStatus"
          optionLabelKey="label"
          options={Object.values(ControlOverLegalClientStatusType).map((v) => ({
            label: t(
              `onboarding.legalClientBeneficiary.controlOver.controlOverLegalClientStatus.types.${v}`
            ),
            value: v,
          }))}
          optionValueKey="value"
          placeholder={t('common.select.placeholder')}
          variant="select"
        />
      )}
      {enabledHasControlOverLegalClient && (
        <Field.Autocomplete
          label={t(
            'onboarding.legalClientBeneficiary.controlOver.hasControlOverLegalClient.label'
          )}
          name="hasControlOverLegalClient"
          optionLabelKey="label"
          options={getYesOrNoOptions(t)}
          optionValueKey="value"
          placeholder={t('common.select.placeholder')}
          variant="select"
        />
      )}
      <Field.Number
        format={(v) => (!v ? v : Number(v).toString())}
        label={t(
          'onboarding.legalClientBeneficiary.controlOver.directCapitalParticipationPercentage.label'
        )}
        maxNumber={100}
        name="directCapitalParticipationPercentage"
        placeholder={t(
          'onboarding.legalClientBeneficiary.controlOver.directCapitalParticipationPercentage.placeholder'
        )}
      />

      <Field.Number
        showOptionalMark
        format={(v) => (!v ? v : Number(v).toString())}
        label={t(
          'onboarding.legalClientBeneficiary.controlOver.indirectCapitalParticipationPercentage.label'
        )}
        maxNumber={100}
        name="indirectCapitalParticipationPercentage"
        placeholder={t(
          'onboarding.legalClientBeneficiary.controlOver.indirectCapitalParticipationPercentage.placeholder'
        )}
      />
      {enabledFinancialConditionAmount && (
        <Field.PriceAndCurrency
          label={t(
            'onboarding.legalClientBeneficiary.controlOver.financialConditionAmount.label'
          )}
          name="financialConditionAmount"
          placeholder={t(
            'onboarding.legalClientBeneficiary.controlOver.financialConditionAmount.placeholder'
          )}
        />
      )}
    </FormContainer>
  );
};
